import { createDebugLogger, enableDebugNamespaces } from '@ahaslides-product/frontend-toolkit'

// Centralised `debug` factory. Every namespace is prefixed with `aha:` so the
// whole app's debug output can be toggled at once from the browser console with:
//   localStorage.debug = 'aha:*'        // all aha namespaces
//   localStorage.debug = 'aha:ppt:*'    // just the PowerPoint add-in
// then reload. Logging is silent unless a matching namespace is enabled, so
// these calls are safe to leave in production code.
const ROOT_NAMESPACE = 'aha'

// On local dev, enable all `aha:*` namespaces by default so logs show without
// any manual setup. Other libs (e.g. the OpenTok SDK) also write their own
// namespaces into `localStorage.debug`, so we APPEND `aha:*` to whatever is
// already there instead of overwriting — preserving their config and ours.
// Other envs stay silent unless opted in.
if (process.env.VUE_APP_ENVAPP === 'local') {
  enableDebugNamespaces(`${ROOT_NAMESPACE}:*`)
}

// A short id generated once per JS context. On PowerPoint for the web a multi-slide
// insert spins up SEVERAL add-in iframes at once — each is its own JS context, so each
// gets its own FRAME_ID — which is exactly what lets you tell their interleaved logs
// apart in a single console (e.g. which frame resolved which slide). Desktop runs a
// single context, so this is noise there; we only prepend it when the PPT-online add-in
// is active (see below).
const FRAME_ID = Math.random().toString(36).slice(2, 8)

// Resolved lazily rather than via a static `import` on purpose: debug.js is a leaf util
// that the store tree itself imports (ppt-addin.actions.js -> createDebug), and
// ppt-online.utils imports the store — a static import here would close that cycle and
// leave `createDebug` undefined at store-init time. By the time any log actually fires
// (runtime, post-init) every module is fully evaluated, so a lazy require is safe.
let isPptOnlineAddinFn = null
const isPptOnlineAddin = () => {
  if (!isPptOnlineAddinFn) {
    try {
      // eslint-disable-next-line global-require
      isPptOnlineAddinFn = require('@/utils/ppt-online.utils').isPptOnlineAddin
    } catch (e) {
      isPptOnlineAddinFn = () => false
    }
  }
  return isPptOnlineAddinFn()
}

const createDebug = (namespace) => {
  const log = createDebugLogger(namespace)
  const wrapped = (...args) => {
    // Guard the store read behind the namespace's own enabled flag so disabled logging
    // stays truly zero-cost (no store lookup on every call site).
    if (log.enabled && isPptOnlineAddin()) {
      log(`[frame ${FRAME_ID}]`, ...args)
    } else {
      log(...args)
    }
  }
  // Preserve the `debug` instance surface for any caller that reads it directly.
  wrapped.log = log
  return wrapped
}

export default createDebug
export { createDebug, FRAME_ID }
